import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

const VideoSection = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.tertiary};
`;

const VideoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.medium};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.purple} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxlarge};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  border-radius: ${props => props.theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.glowStrong};
  aspect-ratio: 16 / 9;
  background: ${props => props.theme.colors.background.card};

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

interface YouTubeSectionProps {
  videoId: string;
  title?: string;
  subtitle?: string;
}

const YouTubeSection: React.FC<YouTubeSectionProps> = ({
  videoId,
  title = "See NeuralPrompt in Action",
  subtitle = "Watch how NeuralPrompt transforms your creative workflow"
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    // Cargar la API de YouTube si no está cargada
    if (!(window as any).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    // Callback cuando la API está lista
    const initPlayer = () => {
      if (videoRef.current && (window as any).YT && (window as any).YT.Player) {
        playerRef.current = new (window as any).YT.Player('youtube-player', {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            enablejsapi: 1,
            mute: 1,
          },
          events: {
            onReady: () => {
              setPlayerReady(true);
            },
          },
        });
      }
    };

    if ((window as any).YT && (window as any).YT.Player) {
      initPlayer();
    } else {
      (window as any).onYouTubeIframeAPIReady = initPlayer;
    }
  }, [videoId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible && playerReady) {
            setIsVisible(true);
            // Reproducir el video cuando sea visible
            setTimeout(() => {
              if (playerRef.current && playerRef.current.playVideo) {
                try {
                  playerRef.current.playVideo();
                } catch (e) {
                  console.log('Autoplay bloqueado por el navegador');
                }
              }
            }, 300);
          } else if (!entry.isIntersecting && isVisible) {
            // Pausar cuando ya no sea visible
            if (playerRef.current && playerRef.current.pauseVideo) {
              playerRef.current.pauseVideo();
            }
          }
        });
      },
      {
        threshold: 0.5, // 50% visible
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [isVisible, playerReady]);

  return (
    <VideoSection id="video">
      <VideoContainer>
        <SectionTitle>{title}</SectionTitle>
        <SectionSubtitle>{subtitle}</SectionSubtitle>
        <VideoWrapper ref={videoRef}>
          <div id="youtube-player"></div>
        </VideoWrapper>
      </VideoContainer>
    </VideoSection>
  );
};

export default YouTubeSection;
